import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import LogoMark from "./LogoMark";

export default function Logo3D() {
    const wrapRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 90, damping: 14 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 90, damping: 14 });
    const glowX = useTransform(mx, [-0.5, 0.5], ["20%", "80%"]);
    const glowY = useTransform(my, [-0.5, 0.5], ["20%", "80%"]);
    const { scrollY } = useScroll();
    const driftY = useTransform(scrollY, [0, 700], [0, 70]);

    const onMove = (e) => {
        const r = wrapRef.current.getBoundingClientRect();
        mx.set((e.clientX - (r.left + r.width / 2)) / r.width);
        my.set((e.clientY - (r.top + r.height / 2)) / r.height);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <motion.div
            ref={wrapRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ y: driftY, perspective: 1200 }}
            className="relative mx-auto flex h-[320px] w-[320px] items-center justify-center sm:h-[420px] sm:w-[420px]"
            data-testid="logo-3d"
        >
            <motion.div
                initial={{ scale: 0.55, opacity: 0, rotateY: -50 }}
                animate={{ scale: 1, opacity: 1, rotateY: 0 }}
                transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className="relative h-full w-full"
            >
                <div className="animate-float relative h-full w-full" style={{ transformStyle: "preserve-3d" }}>
                    {/* ambient glow that follows the cursor */}
                    <motion.div
                        className="absolute inset-[8%] rounded-full blur-3xl"
                        style={{
                            background: `radial-gradient(circle at ${glowX.get()} ${glowY.get()}, rgba(184,75,240,0.35), rgba(255,106,90,0.12) 45%, transparent 70%)`,
                            backgroundColor: "rgba(184,75,240,0.18)",
                        }}
                    />
                    {/* outer ring — rotating arc */}
                    <div
                        className="animate-spin-slow absolute inset-0 rounded-full"
                        style={{
                            background:
                                "conic-gradient(from 0deg, transparent 0deg, transparent 240deg, rgba(255,106,90,0.9) 300deg, rgba(184,75,240,0.9) 330deg, rgba(78,124,255,0.95) 350deg, transparent 360deg)",
                            WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                            mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                            transform: "translateZ(46px)",
                        }}
                    />
                    <div
                        className="animate-spin-rev absolute inset-[7%] rounded-full"
                        style={{
                            background:
                                "conic-gradient(from 140deg, transparent 0deg, transparent 250deg, rgba(255,255,255,0.5) 320deg, transparent 355deg)",
                            WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px))",
                            mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px))",
                            transform: "translateZ(70px)",
                        }}
                    />
                    {/* orbiting satellite */}
                    <div className="animate-spin-slow absolute inset-0" style={{ transform: "translateZ(60px)" }}>
                        <div
                            className="absolute left-1/2 top-[2%] h-3 w-3 -translate-x-1/2 rounded-full shadow-glow"
                            style={{ background: "linear-gradient(135deg,#FF6A5A,#B84BF0,#4E7CFF)" }}
                        />
                    </div>

                    {/* the coin */}
                    <div
                        className="absolute inset-[13%] rounded-full p-[3px]"
                        style={{
                            background: "conic-gradient(from 210deg, #FF6A5A, #B84BF0, #4E7CFF, #B84BF0, #FF6A5A)",
                            transform: "translateZ(20px)",
                            boxShadow: "0 30px 90px -20px rgba(184,75,240,0.55), inset 0 0 30px rgba(255,255,255,0.06)",
                        }}
                    >
                        <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#0a0713]">
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "radial-gradient(circle at 35% 25%, rgba(255,106,90,0.16), transparent 55%), radial-gradient(circle at 70% 80%, rgba(78,124,255,0.18), transparent 55%)",
                                }}
                            />
                            <LogoMark size="58%" glitch className="relative h-[58%] w-[58%] drop-shadow-[0_0_28px_rgba(184,75,240,0.55)]" />
                            {/* light sweep */}
                            <div className="absolute inset-0 overflow-hidden rounded-full">
                                <div className="animate-sheen h-full w-1/3 bg-gradient-to-r from-transparent via-white/12 to-transparent" />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* floor shadow */}
            <div className="absolute -bottom-4 h-10 w-2/3 rounded-full bg-violet-600/25 blur-2xl" />
        </motion.div>
    );
}
